import { useCallback, useEffect, useRef, useState } from "react";
import { styled } from "@glitz/react";
import { spacing, fontSize, Theme, Size, darkGrey } from "../theme";
import Button from "./basic/Button";

const API_BASE = process.env.REACT_APP_API_URL || "";

type CreateResponse = {
  instructionId: string;
  paymentRequestToken: string;
  amount: string;
  currency: string;
  status: string;
  mode?: string;
  qrAvailable?: boolean;
  alreadyPaid?: boolean;
  error?: string;
};

type StatusResponse = {
  instructionId: string;
  orderId: string;
  status: string;
  fulfilled?: boolean;
  error?: string;
};

interface Props {
  orderId: number | string;
  returnUrl?: string;
  onPaid: () => void;
}

function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function buildSwishAppUrl(token: string, callbackUrl: string) {
  const params = new URLSearchParams();
  params.set("token", token);
  params.set("callbackurl", callbackUrl);
  return `swish://paymentrequest?${params.toString()}`;
}

const SwishPaymentPanel: React.FC<Props> = ({ orderId, returnUrl, onPaid }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payment, setPayment] = useState<CreateResponse | null>(null);
  const [status, setStatus] = useState<string>("");
  const [mobile] = useState(() => isMobileDevice());
  const pollRef = useRef<number | null>(null);
  const paidRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const markPaid = useCallback(() => {
    if (paidRef.current) return;
    paidRef.current = true;
    stopPolling();
    setStatus("PAID");
    onPaid();
  }, [onPaid, stopPolling]);

  const pollStatus = useCallback(
    async (instructionId: string) => {
      try {
        const res = await fetch(
          `${API_BASE}/api/payments/swish/${encodeURIComponent(instructionId)}/status`
        );
        const data = (await res.json()) as StatusResponse;
        if (!res.ok) return;
        setStatus(data.status || "");
        if (data.status === "PAID" || data.fulfilled) {
          markPaid();
          return;
        }
        if (["DECLINED", "ERROR", "CANCELLED"].includes(data.status)) {
          stopPolling();
          setError(
            data.status === "DECLINED"
              ? "Betalningen avböjdes i Swish."
              : data.status === "CANCELLED"
                ? "Betalningen avbröts."
                : "Swish-betalningen misslyckades. Försök igen."
          );
        }
      } catch {
        // ignore transient poll errors
      }
    },
    [markPaid, stopPolling]
  );

  const startPolling = useCallback(
    (instructionId: string) => {
      stopPolling();
      void pollStatus(instructionId);
      pollRef.current = window.setInterval(() => {
        void pollStatus(instructionId);
      }, 2500);
    },
    [pollStatus, stopPolling]
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startPayment = async () => {
    setLoading(true);
    setError(null);
    paidRef.current = false;
    try {
      const res = await fetch(`${API_BASE}/api/payments/swish/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });
      const data = (await res.json()) as CreateResponse;
      if (!res.ok) {
        setError(data.error || "Kunde inte starta Swish-betalning");
        return;
      }
      setPayment(data);
      setStatus(data.status || "CREATED");

      if (data.alreadyPaid || data.status === "PAID") {
        markPaid();
        return;
      }

      if (!data.paymentRequestToken) {
        setError(
          "Swish returnerade ingen betalningstoken. Kontrollera att Handel-numret är kopplat korrekt."
        );
        return;
      }

      startPolling(data.instructionId);

      if (mobile) {
        const callback =
          returnUrl ||
          `${window.location.origin}${process.env.PUBLIC_URL || ""}/checkout/pay/${orderId}`;
        window.location.href = buildSwishAppUrl(data.paymentRequestToken, callback);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kunde inte starta Swish-betalning");
    } finally {
      setLoading(false);
    }
  };

  const openSwishAgain = () => {
    if (!payment?.paymentRequestToken) return;
    const callback =
      returnUrl ||
      `${window.location.origin}${process.env.PUBLIC_URL || ""}/checkout/pay/${orderId}`;
    window.location.href = buildSwishAppUrl(payment.paymentRequestToken, callback);
  };

  const qrUrl = payment?.instructionId
    ? `${API_BASE}/api/payments/swish/${encodeURIComponent(payment.instructionId)}/qr`
    : null;

  return (
    <Wrapper>
      <Logo src={`${process.env.PUBLIC_URL || ""}/swish_logo_primary.svg`} alt="Swish" />
      {!payment && (
        <>
          <Text>
            {mobile
              ? "Öppna Swish-appen med belopp och order redan ifyllda."
              : "Visa en QR-kod som du scannar med Swish-appen."}
          </Text>
          <Button as={Theme.Primary} size={Size.Small} onClick={startPayment} disabled={loading}>
            {loading ? "Startar…" : "Betala med Swish"}
          </Button>
        </>
      )}

      {payment && status !== "PAID" && (
        <>
          <Amount>
            {payment.amount} {payment.currency}
          </Amount>
          {mobile ? (
            <>
              <Text>Öppnar Swish… Om appen inte startade, tryck igen.</Text>
              <Button size={Size.Small} onClick={openSwishAgain}>
                Öppna Swish igen
              </Button>
            </>
          ) : (
            <>
              <Text>Scanna QR-koden med Swish-appen på din telefon.</Text>
              {qrUrl && payment.qrAvailable !== false ? (
                <QrImage src={qrUrl} alt="Swish QR-kod" />
              ) : (
                <Text>QR saknas — öppna betalningen på mobilen i stället.</Text>
              )}
            </>
          )}
          <StatusText>
            {status === "CREATED" || !status
              ? "Väntar på betalning…"
              : `Status: ${status}`}
          </StatusText>
        </>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
};

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: spacing.m,
  padding: { y: spacing.s },
  textAlign: "center",
});

const Logo = styled.img({
  height: "48px",
  width: "auto",
});

const Text = styled.p({
  margin: { y: 0 },
  fontSize: fontSize.m,
  lineHeight: 1.5,
  maxWidth: "28rem",
});

const Amount = styled.p({
  margin: { y: 0 },
  fontSize: fontSize.l,
  fontWeight: 600,
});

const QrImage = styled.img({
  width: "220px",
  height: "220px",
  objectFit: "contain",
  backgroundColor: "#fff",
});

const StatusText = styled.p({
  margin: { y: 0 },
  fontSize: fontSize.s,
  color: darkGrey,
});

const ErrorText = styled.p({
  margin: { y: 0 },
  fontSize: fontSize.s,
  color: "#c00",
});

export default SwishPaymentPanel;
